import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { isObject } from '@tuic/lib/helpers/object';
import { logger } from '@tuic/logger';

import { Pcl } from '../../index';
import { CacheTag } from '../../utils';

import type { Locale } from '@tuic/i18n';

export type PclAirlineCollection = Record<string, PclAirline | undefined>;

export type PclAirline = {
  iata: string;
  country?: string | null;
  name: string;
};

/**
 * Returns the Airlines from the PCL.
 */
export const getAirlines = async (
  locale: Locale,
): Promise<PclAirlineCollection | null> => {
  const { data, error } = await Pcl.getAirlines({ query: { locale } });

  if (error) {
    logger.error(`[PCL] Could not fetch airlines. Locale: ${locale}`);
    logger.error(error);

    return {};
  }

  const airlines: PclAirlineCollection = {};

  if (!isObject(data)) {
    return airlines;
  }

  Object.entries(data).forEach(([iataKey, pclAirline]) => {
    if (!isObject(pclAirline)) {
      return;
    }

    const iata = 'iata' in pclAirline ? pclAirline.iata : undefined;
    const country = 'country' in pclAirline ? pclAirline.country : undefined;
    let name = 'name' in pclAirline ? pclAirline.name : undefined;

    if (!name) {
      name = iata;
    }

    airlines[iataKey] = {
      iata: iata ? String(iata) : '',
      country: country ? String(country) : null,
      name: name ? String(name) : '',
    };
  });

  return airlines;
};

/**
 * Returns the Airlines from the PCL.
 */

export const cachedGetAirlines: typeof getAirlines = locale => {
  return remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getAirlines', locale]),
    () => getAirlines(locale),
    {
      tags: [CacheTag.pcl, CacheTag.airlineResource, CacheTag.getAirlines],
      ttl: 600,
    },
  );
};
